import Typography from '@/components/Typography';
import { TextStyles } from '@/constants';
import Link from 'next/link';
import styled from 'styled-components';

// Container only gains its horizontal padding from `md` up, so below that the page
// has to supply its own — without it the cards sit flush against the screen edge.
export const DirectoryWrapper = styled.div(({ theme }) => ({
  padding: `${theme.spacing[32]} ${theme.spacing[16]} ${theme.spacing[48]}`,

  [`${theme.mediaBreakpointUp.md}`]: {
    paddingLeft: 0,
    paddingRight: 0,
  },
}));

export const DirectoryTitle = styled(Typography).attrs({
  variant: TextStyles['Heading 1'],
  type: 'h1',
})(({ theme }) => ({
  color: theme.colors.highEmphasisBlack,
  marginBottom: theme.spacing[8],
}));

export const DirectoryDescription = styled(Typography).attrs({
  variant: TextStyles['Body Text Large'],
})(({ theme }) => ({
  color: theme.colors.mediumEmphasisBlack,
  marginBottom: theme.spacing[24],
}));

export const DirectoryToolbar = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[12],
  marginBottom: theme.spacing[24],

  [`${theme.mediaBreakpointUp.md}`]: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

export const SearchInputWrapper = styled.div(({ theme }) => ({
  position: 'relative',
  width: '100%',

  [`${theme.mediaBreakpointUp.md}`]: {
    maxWidth: '400px',
  },
}));

// *_dark variants: the stock search.svg / expand_more.svg are filled white for use on
// the blue button backgrounds elsewhere, so they are invisible on this white toolbar.
// The dark copies carry mediumEmphasisBlack baked into the asset.
export const SearchIcon = styled.img.attrs({
  src: '/images/icons/search_dark.svg',
  alt: '',
  'aria-hidden': true,
})(({ theme }) => ({
  position: 'absolute',
  left: theme.spacing[12],
  top: '50%',
  transform: 'translateY(-50%)',
  width: theme.spacing[20],
  height: theme.spacing[20],
  pointerEvents: 'none',
}));

export const SearchInput = styled.input(({ theme }) => ({
  width: '100%',
  height: theme.spacing[44],
  // left padding clears the search icon sitting inside the field
  padding: `0 ${theme.spacing[16]} 0 ${theme.spacing[40]}`,
  borderRadius: theme.spacing[4],
  border: `1px solid ${theme.colors.gray01}`,
  backgroundColor: theme.colors.highEmphasisWhite,
  color: theme.colors.highEmphasisBlack,
  fontSize: theme.fontSizes[14],
  lineHeight: theme.lineHeight[22],
  outline: 'none',

  '&:focus': {
    borderColor: theme.colors.blue,
  },

  '&::placeholder': {
    color: theme.colors.disabledBlack,
  },
}));

export const LocaleFilterWrapper = styled.div({
  position: 'relative',
  // on mobile the toolbar stacks, so the button — and the panel anchored to it —
  // span the full row instead of hugging the right edge
  alignSelf: 'stretch',
});

export const LocaleFilterButton = styled.button(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing[8],
  width: '100%',
  height: theme.spacing[44],
  padding: `0 ${theme.spacing[16]}`,
  borderRadius: theme.spacing[4],
  border: `1px solid ${theme.colors.gray01}`,
  backgroundColor: theme.colors.highEmphasisWhite,
  color: theme.colors.highEmphasisBlack,
  cursor: 'pointer',

  '&:hover': {
    borderColor: theme.colors.blue,
  },

  [`${theme.mediaBreakpointUp.md}`]: {
    width: 'auto',
  },
}));

export const LocaleFilterChevron = styled.img.attrs({
  src: '/images/icons/expand_more_dark.svg',
  alt: '',
  'aria-hidden': true,
})<{ isOpen?: boolean }>(({ theme, isOpen }) => ({
  width: theme.spacing[20],
  height: theme.spacing[20],
  transform: isOpen ? 'rotate(180deg)' : 'none',
  transition: 'transform 150ms ease-out',
}));

export const LocaleFilterList = styled.ul(({ theme }) => ({
  position: 'absolute',
  top: `calc(100% + ${theme.spacing[4]})`,
  // full-width under the button on mobile: a 220px panel anchored right would
  // hang off the viewport once the toolbar stacks
  left: 0,
  right: 0,
  zIndex: 2,
  margin: 0,
  padding: theme.spacing[8],
  listStyle: 'none',
  maxHeight: '320px',
  overflowY: 'auto',
  borderRadius: theme.spacing[4],
  border: `1px solid ${theme.colors.gray01}`,
  backgroundColor: theme.colors.highEmphasisWhite,
  boxShadow: theme.elevation[3],

  [`${theme.mediaBreakpointUp.md}`]: {
    left: 'auto',
    minWidth: '220px',
  },
}));

export const LocaleFilterOption = styled(Link).withConfig({
  shouldForwardProp: (propName) => propName.toString() !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  display: 'block',
  padding: `${theme.spacing[8]} ${theme.spacing[12]}`,
  borderRadius: theme.spacing[4],
  color: isActive ? theme.colors.blue : theme.colors.highEmphasisBlack,
  backgroundColor: isActive ? theme.colors.lightBlue : 'transparent',
  textDecoration: 'none',

  '&:hover': {
    backgroundColor: theme.colors.surface02,
  },
}));

export const ResultCount = styled(Typography).attrs({
  variant: TextStyles['Body Text Small'],
})(({ theme }) => ({
  color: theme.colors.mediumEmphasisBlack,
  marginBottom: theme.spacing[16],
}));

export const DirectoryGrid = styled.ul<{ isPending?: boolean }>(({ theme, isPending }) => ({
  display: 'grid',
  opacity: isPending ? 0.5 : 1,
  pointerEvents: isPending ? 'none' : undefined,
  transition: 'opacity 150ms ease-out',
  gridTemplateColumns: 'repeat(1, 1fr)',
  gap: theme.spacing[12],
  margin: 0,
  padding: 0,
  listStyle: 'none',

  // Grid items default to min-width:auto, so a long title makes its 1fr track wider
  // than its share and pushes the last column off-screen. Let the tracks stay equal
  // and clamp the title instead.
  '> li': {
    minWidth: 0,
  },

  // Two columns start at md, not sm: a 375px-wide phone splits into ~165px cards, and
  // after the logo and padding that leaves ~80px of title — every card reads "2660 Express…".
  [`${theme.mediaBreakpointUp.md}`]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },

  [`${theme.mediaBreakpointUp.lg}`]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },

  [`${theme.mediaBreakpointUp.xl}`]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
}));

export const DirectoryCardLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[12],
  padding: theme.spacing[16],
  borderRadius: theme.spacing[8],
  border: `1px solid ${theme.colors.gray01}`,
  backgroundColor: theme.colors.highEmphasisWhite,
  textDecoration: 'none',
  height: '100%',

  '&:hover': {
    borderColor: theme.colors.blue,
    boxShadow: theme.elevation[2],
  },
}));

export const DirectoryCardLogo = styled.span(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: theme.spacing[40],
  height: theme.spacing[40],
  flexShrink: 0,

  img: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
}));

// The card body owns the remaining width so a long title clamps instead of
// pushing the language badge out of the card.
export const DirectoryCardBody = styled.span(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing[4],
  minWidth: 0,
}));

export const DirectoryCardTitle = styled(Typography).attrs({
  variant: TextStyles['Heading 5'],
  type: 'span',
})(({ theme }) => ({
  color: theme.colors.highEmphasisBlack,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  // carrier names run long and unhyphenated ("Adelaide Direct Stationers"); without this
  // a single word can still outgrow the card on the narrowest breakpoint
  overflowWrap: 'anywhere',
}));

/**
 * The all-languages view lists the same carrier once per locale ("Agility Tracking",
 * "Agility 追跡 — 荷物トラッキング", …). The badge is what tells those apart at a glance;
 * without it the list reads as duplicated rows.
 */
export const LanguageBadge = styled(Typography).attrs({
  variant: TextStyles['Caption Text'],
  type: 'span',
})(({ theme }) => ({
  padding: `0 ${theme.spacing[6]}`,
  borderRadius: theme.spacing[4],
  backgroundColor: theme.colors.surface02,
  color: theme.colors.mediumEmphasisBlack,
  whiteSpace: 'nowrap',
}));

export const NoResultsText = styled(Typography).attrs({
  variant: TextStyles['Body Text Large'],
})(({ theme }) => ({
  color: theme.colors.mediumEmphasisBlack,
  padding: `${theme.spacing[32]} 0`,
  textAlign: 'center',
}));
